<template>
  <div class="mb-8">
    <v-expansion-panels accordion>
      <v-expansion-panel class="custom_card">
        <v-expansion-panel-header class="d-flex">
          <div class="d-flex" style="gap: 10px;">
            <v-icon :color="item.color ? item.color.hex : null" small>
              fiber_manual_record
            </v-icon>
            {{ item.name }}
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row no-gutters>
              <!------------------------------------------>
              <!-- NAME ----------------------------->
              <!------------------------------------------>
              <v-col cols="12" md="5">
                <span class="subtitle-2">{{ inputs.planName.title }}</span>
                <v-text-field
                  v-model="localSavedPlan.name"
                  :placeholder="inputs.planName.placeholder"
                  class="rounded-lg mt-1"
                  outlined
                  dense
                  :rules="inputs.planName.rules"
                  required
                >
                  <v-tooltip slot="append" bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" color="grey lighten-1" size="22"
                        >help</v-icon
                      >
                    </template>
                    <span style="max-width: 200px;">{{
                      inputs.planName.tooltip
                    }}</span>
                  </v-tooltip>
                </v-text-field>
              </v-col>
              <!------------------------------------------>
              <!-- FREQUENCY ----------------------------->
              <!------------------------------------------>
              <v-col cols="6" md="3" class="text-center ml-4">
                <span class="subtitle-2">{{ inputs.planFrequency.title }}</span>
                <v-select
                  v-if="pricingModel.frequency === 3"
                  v-model="localSavedPlan.frequency"
                  :items="getFrequencyOptions"
                  :placeholder="inputs.planFrequency.title"
                  item-text="title"
                  item-value="value"
                  class="rounded-lg mt-1"
                  :rules="inputs.planFrequency.rules"
                  outlined
                  dense
                ></v-select>

                <div v-else>
                  <p class="subtitle-2 font-weight-regular mt-3">
                    {{ getFrequencyName(localSavedPlan.frequency) }}
                  </p>
                </div>
              </v-col>
              <!------------------------------------------>
              <!-- COLOR ----------------------------->
              <!------------------------------------------>
              <v-col cols="6" md="3" class="color-picker-column">
                <span class="subtitle-2 text-center">{{
                  inputs.planColor.title
                }}</span>
                <div
                  class="d-flex justify-center align-center mt-2"
                  style="gap: 8px;"
                >
                  <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                      <div v-on="on">
                        <v-icon v-if="!localSavedPlan.color" color="grey"
                          >palette</v-icon
                        >
                        <div
                          v-if="localSavedPlan.color"
                          class="color-swatch"
                          :style="{ backgroundColor: localSavedPlan.color.hex }"
                        ></div>
                      </div>
                    </template>

                    <v-color-picker
                      v-model="localSavedPlan.color"
                      dot-size="25"
                      hide-canvas
                      hide-inputs
                      hide-mode-switch
                      hide-sliders
                      show-swatches
                      swatches-max-height="200"
                    ></v-color-picker>
                  </v-menu>
                </div>
              </v-col>
              <!------------------------------------------>
              <!-- DESCRIPTION ----------------------------->
              <!------------------------------------------>
              <v-col cols="12" md="12">
                <span class="subtitle-2">{{
                  inputs.planDescription.title
                }}</span>
                <v-text-field
                  v-model="localSavedPlan.description"
                  :placeholder="inputs.planDescription.placeholder"
                  class="rounded-lg mt-1"
                  outlined
                  dense
                  :rules="inputs.planDescription.rules"
                  required
                >
                  <v-tooltip slot="append" bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" color="grey lighten-1" size="22"
                        >help</v-icon
                      >
                    </template>
                    <span style="max-width: 200px;">{{
                      inputs.planDescription.tooltip
                    }}</span>
                  </v-tooltip>
                </v-text-field>
              </v-col>

              <v-col cols="12" class="d-flex my-4">
                <p class="primary-color subtitle-2">
                  {{ $t('pricing_model.create.step_2.plan_settings') }}
                </p>
                <v-divider
                  class="mt-3"
                  inset
                  style="border-color: #2661ff;"
                ></v-divider>
              </v-col>

              <!------------------------------------------>
              <!-- PLANS BY CURRENCY ----------------------------->
              <!------------------------------------------>
              <v-col cols="12">
                <v-row
                  v-for="(currencyPlan,
                  currencyByPlanIndex) in localSavedPlan.plansByCurrency"
                  :key="currencyByPlanIndex"
                >
                  <!------------------------------------------>
                  <!-- CURRENCIES ----------------------------->
                  <!------------------------------------------>
                  <v-col cols="12" md="2">
                    <p class="subtitle-2">
                      {{ inputs.planCurrency.title }}
                      <v-tooltip slot="append" bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" color="grey lighten-1" size="22"
                            >help</v-icon
                          >
                        </template>
                        <span style="max-width: 200px;">{{
                          inputs.planDescription.tooltip
                        }}</span>
                      </v-tooltip>
                    </p>
                    <span class="text-center">
                      {{ getCurrencyCode(currencyPlan.currency_id) }}
                    </span>
                  </v-col>

                  <v-col cols="12">
                    <v-card
                      class="rounded-xl pa-4 mb-4"
                      flat
                      style="border: 1px solid #BCBCBC"
                    >
                      <v-card-text>
                        <v-row>
                          <!------------------------------------------>
                          <!-- AMOUNT ----------------------------->
                          <!------------------------------------------>
                          <v-col cols="12" md="6">
                            <div class="d-flex align-center">
                              <span class="subtitle-2"
                                >{{ inputs.planAmount.title }}
                                <v-tooltip slot="append" bottom>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      v-on="on"
                                      color="grey lighten-1"
                                      size="18"
                                      >help</v-icon
                                    >
                                  </template>
                                  <span style="max-width: 200px;">
                                    {{ inputs.planAmount.tooltip }}
                                  </span>
                                </v-tooltip></span
                              >
                              <v-switch
                                v-model="currencyPlan.hasCustomAmount"
                                :disabled="
                                  pricingModel.hasLimitsTable ||
                                    pricingModelVersion2
                                "
                                label="Monto variable"
                                color="#2661FF"
                                inset
                                class="ml-6"
                                @change="
                                  customAmount(
                                    localSavedPlan,
                                    currencyPlan.currency_id
                                  )
                                "
                              ></v-switch>
                            </div>
                            <v-text-field
                              v-if="!currencyPlan.hasCustomAmount"
                              v-model="currencyPlan.amount"
                              :placeholder="inputs.planAmount.placeholder"
                              class="rounded-lg mt-1"
                              outlined
                              dense
                              :disabled="currencyPlan.hasCustomAmount"
                              :rules="
                                !currencyPlan.hasCustomAmount
                                  ? inputs.planAmount.rules
                                  : ''
                              "
                              required
                            ></v-text-field>
                          </v-col>
                          <!------------------------------------------>
                          <!-- FEE_PERCENTAGE ----------------------------->
                          <!------------------------------------------>
                          <v-col cols="12" md="6">
                            <div class="d-flex align-center">
                              <span class="subtitle-2"
                                >{{ inputs.planFee.title }}
                                <v-tooltip slot="append" bottom>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      v-on="on"
                                      color="grey lighten-1"
                                      size="18"
                                      >help</v-icon
                                    >
                                  </template>
                                  <span style="max-width: 200px;">
                                    {{ inputs.planFee.tooltip }}
                                  </span>
                                </v-tooltip></span
                              >
                              <v-switch
                                v-model="currencyPlan.hasCustomFee"
                                :disabled="
                                  pricingModel.hasLimitsTable ||
                                    pricingModelVersion2
                                "
                                label="Fee variable"
                                color="#2661FF"
                                inset
                                class="ml-6"
                                @change="
                                  customFee(
                                    localSavedPlan,
                                    currencyPlan.currency_id
                                  )
                                "
                              ></v-switch>
                            </div>
                            <v-text-field
                              v-if="!currencyPlan.hasCustomFee"
                              v-model="currencyPlan.fee_percentage"
                              :placeholder="inputs.planFee.placeholder"
                              class="rounded-lg mt-1"
                              outlined
                              dense
                              :rules="inputs.planFee.rules"
                              required
                            ></v-text-field>
                          </v-col>

                          <!------------------------------------------>
                          <!-- LIMITS TABLE ----------------------------->
                          <!------------------------------------------>
                          <v-col cols="12">
                            <v-row v-if="pricingModel.hasLimitsTable">
                              <!------------------------------------------>
                              <!-- LIMIT REGISTERS ----------------------------->
                              <!------------------------------------------>
                              <v-col cols="12" v-if="!pricingModelVersion2">
                                <div class="d-flex align-center">
                                  <span class="subtitle-2"
                                    >{{ inputs.planLimitRegisters.title }}
                                    <v-tooltip slot="append" bottom>
                                      <template v-slot:activator="{ on }">
                                        <v-icon
                                          v-on="on"
                                          color="grey lighten-1"
                                          size="18"
                                        >
                                          help
                                        </v-icon>
                                      </template>
                                      <span style="max-width: 200px;">
                                        {{ inputs.planLimitRegisters.tooltip }}
                                      </span>
                                    </v-tooltip>
                                  </span>
                                </div>
                                <v-row>
                                  <v-col cols="12" md="6" class="mt-4 d-flex">
                                    <span class="mr-4 mt-3">
                                      {{ inputs.planLimitRegisters.from }}
                                    </span>
                                    <v-text-field
                                      v-model="currencyPlan.registers_offset"
                                      placeholder="Sin límite"
                                      class="rounded-lg mt-1"
                                      outlined
                                      disabled
                                      dense
                                      required
                                    ></v-text-field>
                                  </v-col>
                                  <v-col cols="12" md="6" class="mt-4 d-flex">
                                    <span class="mr-4 mt-3">
                                      {{ inputs.planLimitRegisters.to }}
                                    </span>
                                    <v-text-field
                                      v-model="currencyPlan.registers_limit"
                                      :placeholder="
                                        inputs.planLimitRegisters.placeholder
                                      "
                                      class="rounded-lg mt-1"
                                      outlined
                                      type="number"
                                      dense
                                      disabled
                                      required
                                    ></v-text-field>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <!------------------------------------------>
                              <!-- LIMIT INCOMES ----------------------------->
                              <!------------------------------------------>
                              <v-col cols="12">
                                <div class="d-flex align-center">
                                  <span class="subtitle-2"
                                    >{{ inputs.planLimitIncomes.title }}
                                    <v-tooltip slot="append" bottom>
                                      <template v-slot:activator="{ on }">
                                        <v-icon
                                          v-on="on"
                                          color="grey lighten-1"
                                          size="18"
                                          >help</v-icon
                                        >
                                      </template>
                                      <span style="max-width: 200px;">
                                        {{ inputs.planLimitIncomes.tooltip }}
                                      </span>
                                    </v-tooltip></span
                                  >
                                </div>
                                <v-row>
                                  <v-col cols="12" md="6" class="mt-4 d-flex">
                                    <span class="mr-4 mt-3">
                                      {{ inputs.planLimitIncomes.from }}
                                    </span>
                                    <v-text-field
                                      v-model="currencyPlan.incomes_offset"
                                      placeholder="Sin límite"
                                      class="rounded-lg mt-1"
                                      dense
                                      outlined
                                      disabled
                                      required
                                    ></v-text-field>
                                  </v-col>
                                  <v-col cols="12" md="6" class="mt-4 d-flex">
                                    <span class="mr-4 mt-3">
                                      {{ inputs.planLimitIncomes.to }}
                                    </span>
                                    <v-text-field
                                      v-model="currencyPlan.incomes_limit"
                                      :placeholder="
                                        inputs.planLimitIncomes.placeholder
                                      "
                                      class="rounded-lg mt-1"
                                      type="number"
                                      dense
                                      outlined
                                      disabled
                                      required
                                    ></v-text-field>
                                  </v-col>
                                </v-row>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-card-text>
                      <v-divider></v-divider>
                      <!------------------------------------------>
                      <!-- ADDITIONAL SERVICES ----------------------------->
                      <!------------------------------------------>
                      <v-row class="my-4">
                        <v-col cols="12">
                          <p class="subtitle-2">
                            {{ inputs.planAdditionalServices.title }}
                            <v-tooltip slot="append" bottom>
                              <template v-slot:activator="{ on }">
                                <v-icon
                                  v-on="on"
                                  color="grey lighten-1"
                                  size="18"
                                  >help</v-icon
                                >
                              </template>
                              <span style="max-width: 200px;">
                                {{ inputs.planAdditionalServices.tooltip }}
                              </span>
                            </v-tooltip>
                          </p>

                          <v-menu
                            ref="menu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on }">
                              <v-btn
                                class="rounded-xl"
                                small
                                outlined
                                color="#2661FF"
                                v-on="on"
                              >
                                <v-icon>add</v-icon>
                                Agregar
                              </v-btn>
                            </template>
                            <v-card class="rounded-lg" flat width="250">
                              <v-list dense>
                                <v-list-item-group
                                  v-model="currencyPlan.selected_services"
                                  multiple
                                  color="#2661FF"
                                >
                                  <v-list-item
                                    v-for="(item, i) in additionalServices"
                                    :key="i"
                                    @click="
                                      toggleService(
                                        item,
                                        localSavedPlan,
                                        currencyPlan
                                      )
                                    "
                                  >
                                    <template v-slot:default="{ active }">
                                      <v-list-item-action>
                                        <v-checkbox
                                          :input-value="active"
                                          color="#2661FF"
                                        ></v-checkbox>
                                      </v-list-item-action>
                                      <v-list-item-content>
                                        <v-list-item-title class="subtitle-2">
                                          {{
                                            $t(
                                              `pricing_model.create.step_2.services.types.${item.name}`
                                            )
                                          }}
                                        </v-list-item-title>
                                      </v-list-item-content>
                                    </template>
                                  </v-list-item>
                                </v-list-item-group>
                              </v-list>
                            </v-card>
                          </v-menu>
                          <v-row>
                            <v-col
                              cols="12"
                              md="4"
                              class="mt-4"
                              v-for="(service,
                              service_index) in currencyPlan.additional_services"
                              :key="service_index"
                            >
                              <span class="subtitle-2">
                                {{
                                  $t(
                                    `pricing_model.create.step_2.services.types.${service.name}`
                                  )
                                }}
                              </span>
                              <v-text-field
                                v-if="currencyPlan.additional_services"
                                v-model="service.amount_per_action"
                                :placeholder="
                                  inputs.planAdditionalServices.placeholder
                                "
                                class="rounded-lg"
                                outlined
                                dense
                                :rules="inputs.planAdditionalServices.rules"
                                required
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>

              <!------------------------------------------>
              <!-- ACTIONS ----------------------------->
              <!------------------------------------------>
              <v-col
                cols="12"
                class="d-flex justify-end align-center"
                style="gap: 24px;"
              >
                <v-btn
                  v-if="hasLimitsTable(item)"
                  @click="removePlan"
                  outlined
                  color="#F04438"
                  class="rounded-lg"
                >
                  <v-icon color="#F04438">
                    delete
                  </v-icon>
                  <span style="color: #F04438;">{{
                    $t('actions.delete')
                  }}</span>
                </v-btn>
                <v-btn @click="update" color="#2661FF" class="rounded-lg my-2">
                  <span style="color: white;">{{ $t('actions.save') }}</span>
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
export default {
  name: 'PricingModelStep2Panel',
  props: {
    item: {
      type: Object,
      required: true,
      default: () => {}
    },
    index: {
      type: Number,
      required: true,
      default: null
    }
  },
  data() {
    return {
      valid: true,
      frequencies: [
        {
          title: this.$t('pricing_model.create.step_1.frequency.monthly'),
          value: 1
        },
        {
          title: this.$t('pricing_model.create.step_1.frequency.yearly'),
          value: 2
        }
      ],
      inputs: {
        planName: {
          title: this.$t('pricing_model.create.step_2.name.title'),
          placeholder: this.$t('pricing_model.create.step_2.name.title'),
          tooltip: this.$t('pricing_model.create.step_2.name.tooltip'),
          rules: [
            v => !!v || this.$t('pricing_model.create.step_2.name.rule'),
            v => {
              // Check if the name is different from the savedPlan at the specified index
              if (
                this.localSavedPlan.name !== this.savedPlans[this.index].name
              ) {
                const existingPlan = this.savedPlans.find(
                  plan => plan.name === v
                )
                return (
                  !existingPlan ||
                  this.$t('pricing_model.create.step_2.name.error')
                )
              }
              return true // Return true if the name is the same as the savedPlan
            }
          ]
        },
        planFrequency: {
          title: this.$t('pricing_model.create.step_2.frequency.title'),
          rules: [
            v => !!v || this.$t('pricing_model.create.step_2.frequency.rule')
          ]
        },
        planColor: {
          title: this.$t('pricing_model.create.step_2.color.title')
        },
        planDescription: {
          title: this.$t('pricing_model.create.step_2.description.title'),
          placeholder: this.$t('pricing_model.create.step_2.description.title'),
          tooltip: this.$t('pricing_model.create.step_2.description.tooltip'),
          rules: [
            v =>
              (!!v && v.length > 10) ||
              this.$t('pricing_model.create.step_2.description.rule')
          ]
        },
        planLimitRegisters: {
          title: this.$t('pricing_model.create.step_2.registers.title'),
          from: this.$t('pricing_model.create.step_2.registers.from'),
          to: this.$t('pricing_model.create.step_2.registers.to'),
          tooltip: this.$t('pricing_model.create.step_2.registers.placeholder'),
          placeholder: this.$t(
            'pricing_model.create.step_2.registers.no_limits'
          )
        },
        planLimitIncomes: {
          title: this.$t('pricing_model.create.step_2.incomes.title'),
          from: this.$t('pricing_model.create.step_2.incomes.from'),
          to: this.$t('pricing_model.create.step_2.incomes.to'),
          tooltip: this.$t('pricing_model.create.step_2.incomes.tooltip'),
          placeholder: this.$t('pricing_model.create.step_2.incomes.no_limits')
        },
        planCurrency: {
          title: this.$t('pricing_model.create.step_2.currency.title'),
          tooltip: this.$t('pricing_model.create.step_2.currency.tooltip'),
          rules: [
            v => !!v || this.$t('pricing_model.create.step_2.currency.rule')
          ]
        },
        planAmount: {
          title: this.$t('pricing_model.create.step_2.amount.title'),
          placeholder: this.$t(
            'pricing_model.create.step_2.amount.placeholder'
          ),
          tooltip: this.$t('pricing_model.create.step_2.amount.tooltip'),
          rules: [
            v =>
              /^\d+(\.\d+)?$/.test(v) ||
              v === null ||
              this.$t('pricing_model.create.step_2.amount.rules.number'),
            v =>
              (v && v.toString().split('.').length < 2) ||
              (v && v.toString().split('.')[1].length <= 2) ||
              this.$t('pricing_model.create.step_2.amount.rules.decimal'),
            v =>
              (v && v >= 0) ||
              this.$t('pricing_model.create.step_2.amount.rules.min')
          ]
        },
        planFee: {
          title: this.$t('pricing_model.create.step_2.fee_percentage.title'),
          placeholder: this.$t(
            'pricing_model.create.step_2.fee_percentage.placeholder'
          ),
          tooltip: this.$t(
            'pricing_model.create.step_2.fee_percentage.tooltip'
          ),
          rules: [
            v =>
              !!v ||
              this.$t(
                'pricing_model.create.step_2.fee_percentage.rules.required'
              ),
            v =>
              /^\d+(\.\d+)?$/.test(v) ||
              this.$t(
                'pricing_model.create.step_2.fee_percentage.rules.number'
              ),
            v =>
              (v && v.toString().split('.').length < 2) ||
              (v && v.toString().split('.')[1].length <= 2) ||
              this.$t(
                'pricing_model.create.step_2.fee_percentage.rules.decimal'
              ),
            v =>
              (v && v >= 0) ||
              this.$t('pricing_model.create.step_2.fee_percentage.rules.min'),
            v =>
              (v && v <= 100) ||
              this.$t('pricing_model.create.step_2.fee_percentage.rules.max')
          ]
        },
        planAdditionalServices: {
          title: this.$t('pricing_model.create.step_2.services.title'),
          tooltip: this.$t('pricing_model.create.step_2.services.tooltip'),
          placeholder: this.$t(
            'pricing_model.create.step_2.services.placeholder'
          ),
          rules: [
            v =>
              /^\d+(\.\d+)?$/.test(v) ||
              v === null ||
              this.$t('pricing_model.create.step_2.services.rule.number'),
            v =>
              (v && v.toString().split('.').length < 2) ||
              (v && v.toString().split('.')[1].length <= 6) ||
              'No more than 6 digits after the decimal point',
            v => (v && v >= 0) || 'Min should be above 0'
          ]
        }
      },
      localSavedPlan: []
    }
  },
  created() {
    this.localSavedPlan = JSON.parse(JSON.stringify(this.item))
  },
  computed: {
    ...mapState({
      savedPlans: ({ pricingModels: { savedPlans } }) => savedPlans,
      pricingModel: ({ pricingModels: { pricingModel } }) => pricingModel,
      additionalServices: ({ pricingModels: { additionalServices } }) =>
        additionalServices,
      currencies: ({ countries: { currencies } }) => currencies
    }),
    getFrequencyOptions() {
      return this.frequencies
    },
    pricingModelVersion2() {
      return this.pricingModel.version === 2
    }
  },
  methods: {
    ...mapActions({}),
    ...mapMutations({
      removeSavedPlan: 'pricingModels/removeSavedPlan',
      updateSavedPlan: 'pricingModels/updateSavedPlan'
    }),
    getFrequencyName(frequency) {
      const matchedFrequency = this.frequencies.find(
        freq => freq.value === frequency
      )
      return matchedFrequency
        ? matchedFrequency.title
        : this.$t('pricing_model.create.step_2.frequency.unknown_frequency')
    },
    hasLimitsTable(plan) {
      // Validate if hasLimitsTable to only allow to delete from the last saved plan to prevent breaking the table, else if the pricing model hasn't table of limits the user can delete in any order
      const lastIndex = this.savedPlans.length - 1

      // Check if the plan is the last one and pricingModel hasLimitsTable is true, or pricingModel has no limits table
      return (
        (this.savedPlans[lastIndex] === plan &&
          this.pricingModel.hasLimitsTable) ||
        !this.pricingModel.hasLimitsTable
      )
    },
    getCurrencyCode(currency_id) {
      const currency = this.currencies.find(
        currency => currency.id === currency_id
      )
      return currency.code
    },
    customAmount(plan, currency_id) {
      const planByCurrency = this.getPlanByCurrency(plan, currency_id)
      if (planByCurrency.hasCustomAmount) {
        planByCurrency.amount = null
      }
    },
    customFee(plan, currency_id) {
      const planByCurrency = this.getPlanByCurrency(plan, currency_id)
      if (planByCurrency.hasCustomFee) {
        planByCurrency.fee_percentage = null
      }
    },
    getPlanByCurrency(plan, currency_id) {
      return plan.plansByCurrency.find(item => item.currency_id === currency_id)
    },
    toggleService(item, plan, currencyPlan) {
      const planByCurrency = this.getPlanByCurrency(
        plan,
        currencyPlan.currency_id
      )

      const index = planByCurrency.additional_services.findIndex(
        selectedItem => selectedItem.name === item.name
      )

      if (index !== -1) {
        planByCurrency.additional_services.splice(index, 1) // Item exists, remove it
      } else {
        // Service doesn't exist, add it
        const newService = {
          ...item, // Copy properties from item
          active: true, // Set initial active state as true or false as needed
          amount_per_action: '' // Modify as needed
        }
        this.$set(
          planByCurrency.additional_services,
          planByCurrency.additional_services.length,
          newService
        )
      }
    },
    async update() {
      try {
        if (await this.$refs.form.validate()) {
          const { localSavedPlan, index } = this

          this.updateSavedPlan({ localSavedPlan, index })
          this.$noty.success(
            this.$t('pricing_model.messages.edit_plan_success')
          )
        } else {
          this.$noty.error(this.$t('pricing_model.messages.verifiy_data'))
        }
      } catch (error) {
        this.$noty.error(this.$t('pricing_model.messages.edit_plan_error'))
      }
    },
    removePlan() {
      this.removeSavedPlan(this.index)
      if (this.pricingModel.hasLimitsTable) {
        this.$emit('setDeletePlanOffset', this.item, 'registers') // Update the registers_offset
        this.$emit('setDeletePlanOffset', this.item, 'incomes') // Update the incomes_offset
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.custom_card {
  border-radius: 12px !important;
  border: 1px solid #d0d3db !important;
}

.primary-color {
  color: #2661ff;
}

.theme--light.v-divider {
  border-color: #bdbdbd !important;
}

.color-picker-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 20px;
  height: 100%;
}

.color-swatch {
  cursor: pointer;
  height: 25px;
  width: 25px;
  border: 2px solid #bdbdbd;
  /* Border styling */
  border-radius: 20px;
  /* To make it a circle */
  transition: border-radius 200ms ease-in-out;
}
</style>
